import React, { useState } from "react";
import axios from "axios";
import { Icon } from "../Icon";

interface TimezoneSettingsProps {
  user: {
    id: string;
    username: string;
    timezone?: string;
  };
  authToken: string;
}

const TimezoneSettings: React.FC<TimezoneSettingsProps> = ({
  user,
  authToken,
}) => {
  const [timezone, setTimezone] = useState<string>(user.timezone || "UTC");
  const [updatingTimezone, setUpdatingTimezone] = useState(false);

  const updateTimezone = async (newTimezone: string) => {
    setUpdatingTimezone(true);
    try {
      console.log("Updating timezone to:", newTimezone);
      const response = await axios.put(
        "/api/user/timezone",
        { timezone: newTimezone },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      console.log("Timezone update response:", response.data);
      setTimezone(newTimezone);
    } catch (error: any) {
      console.error("Error updating timezone:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Unknown error";
      alert(`Failed to update timezone: ${errorMessage}`);
    } finally {
      setUpdatingTimezone(false);
    }
  };

  return (
    <div style={{ maxWidth: "800px" }}>
      <h3 style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}>
        Timezone Settings
      </h3>

      {/* Timezone Settings Panel */}
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "24px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #4a5568",
        }}
      >
        <h4
          style={{ margin: "0 0 16px 0", color: "#9146FF", fontSize: "18px" }}
        >
          <Icon
            name="globe"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Configure Your Timezone
        </h4>

        <p
          style={{
            color: "#cbd5e0",
            marginBottom: "20px",
            lineHeight: "1.6",
          }}
        >
          Select your timezone to ensure accurate timestamps and scheduling for
          all bot features.
        </p>

        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              color: "#cbd5e0",
              fontSize: "14px",
            }}
          >
            Your Timezone
          </label>
          <div style={{ position: "relative" }}>
            <select
              value={timezone}
              onChange={(e) => updateTimezone(e.target.value)}
              disabled={updatingTimezone}
              style={{
                width: "100%",
                padding: "8px 32px 8px 12px",
                border: "1px solid #4a5568",
                borderRadius: "6px",
                backgroundColor: updatingTimezone ? "#2d3748" : "#1a202c",
                color: updatingTimezone ? "#718096" : "#e2e8f0",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                boxSizing: "border-box",
                cursor: updatingTimezone ? "not-allowed" : "pointer",
              }}
            >
              <option value="UTC">UTC - Coordinated Universal Time</option>
              <option value="America/New_York">
                Eastern Time (US & Canada)
              </option>
              <option value="America/Chicago">
                Central Time (US & Canada)
              </option>
              <option value="America/Denver">
                Mountain Time (US & Canada)
              </option>
              <option value="America/Los_Angeles">
                Pacific Time (US & Canada)
              </option>
              <option value="America/Phoenix">Arizona</option>
              <option value="America/Anchorage">Alaska</option>
              <option value="Pacific/Honolulu">Hawaii</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Europe/Berlin">Berlin</option>
              <option value="Europe/Rome">Rome</option>
              <option value="Europe/Madrid">Madrid</option>
              <option value="Europe/Moscow">Moscow</option>
              <option value="Asia/Tokyo">Tokyo</option>
              <option value="Asia/Shanghai">Shanghai</option>
              <option value="Asia/Seoul">Seoul</option>
              <option value="Asia/Kolkata">New Delhi</option>
              <option value="Asia/Dubai">Dubai</option>
              <option value="Australia/Sydney">Sydney</option>
              <option value="Australia/Melbourne">Melbourne</option>
              <option value="Pacific/Auckland">Auckland</option>
            </select>
            <div
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none",
                color: "#cbd5e0",
                fontSize: "12px",
              }}
            >
              {updatingTimezone ? "⏳" : "▼"}
            </div>
          </div>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            fontSize: "12px",
            color: "#a0aec0",
          }}
        >
          <strong>Current Time:</strong>{" "}
          {new Date().toLocaleString("en-US", {
            timeZone: timezone,
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZoneName: "short",
          })}
        </div>
      </div>

      {/* Info Panel */}
      <div
        style={{
          backgroundColor: "rgba(145, 70, 255, 0.1)",
          border: "1px solid rgba(145, 70, 255, 0.3)",
          padding: "16px",
          borderRadius: "8px",
        }}
      >
        <h4 style={{ color: "#9146FF", margin: "0 0 8px 0", fontSize: "16px" }}>
          <Icon
            name="lightbulb"
            size={16}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Why Timezone Matters
        </h4>
        <ul
          style={{
            color: "#cbd5e0",
            fontSize: "14px",
            margin: "0",
            paddingLeft: "20px",
            lineHeight: "1.8",
          }}
        >
          <li>Scheduled triggers run at the correct local time</li>
          <li>Analytics and reports show timestamps in your timezone</li>
          <li>Daily summaries are delivered at appropriate times</li>
          <li>Stream schedules display accurately</li>
        </ul>
      </div>
    </div>
  );
};

export default TimezoneSettings;
